import { batch } from 'react-redux';
import { resetPluCallback, setPluCallback } from 'src/bottom-area/business-logic/handle-plu-items';
import { sellPluItem } from 'src/bottom-area/business-logic/sell-plu-item';
import { EmeraldError } from 'src/common/api/emerald-error';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { ItemNotFound } from 'src/common/helpers/error-code';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { ISelectedItem } from 'src/common/interfaces/dtos/ISelectedItem';
import { IItemInputData } from 'src/common/interfaces/IItemInputData';
import { POSModeActions } from 'src/common/redux/reducers/pos-mode-reducer';
import { SalesKeypadActions } from 'src/common/redux/reducers/sales-keypad-reducer';
import { ScaleStatus } from 'src/devices/device-enums';
import { addItemToTransaction } from 'src/home-page/business-logic/sale-mode-router';
import { formatScannedData } from 'src/home-page/business-logic/submit-scanned-data';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { ActivityType } from 'src/operation-notify/enum/activities.enum';
import { operationNotify } from 'src/operation-notify/operation-notify';
import { showTenderAmountEligibilityModal } from 'src/price-override/business-logic/handle-price-override';
import { addInputData } from 'src/sales-keypad/business-logic/handle-sales-keypad-actions';
import { sellFoundItem } from 'src/search-products/business-logic/sell-found-item';
import {
  resetSearchCallback,
  searchProduct as onSearch,
  setSearchCallback,
} from 'src/search/business-logic/handle-search';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { restorePage } from '../../common/components/emerald-home-page/emerald-home-page';
import * as errorCodes from '../../common/helpers/error-code';
import { store } from '../../common/redux/configure-store';
import { showReturnItemPage } from '../components/return-item';

export const handleReturnItem = async (): Promise<void> => {
  const finishReturnItem = (): void => {
    restorePage();
    resetSearchCallback();
    resetPluCallback();
    batch((): void => {
      store.dispatch(POSModeActions.setReturnState(undefined));
    });
  };

  const onError = (err: EmeraldError): void => {
    if (err instanceof AbortedError && err.code === ItemNotFound) {
      return; // does not close return item page when cancel from Item not found
    }

    if (err.code === errorCodes.TenderAmountExceededEligibilityAmount) {
      showTenderAmountEligibilityModal(err);
    } else {
      popupMessage(err);
    }
    finishReturnItem();
  };

  const onKeypadSumbit = async (input?: string): Promise<void> => {
    if (input) {
      return addInputData(input)
        .then(finishReturnItem)
        .catch(onError);
    }
  };

  const searchCallback = async (
    itemLookup: IItemLookup,
    quantity: number,
    isWeight: boolean,
  ): Promise<void> => {
    const scale = store.getState().scale;
    // If there's a weight on the scale, us that rather than manual weight.
    if (scale.status === ScaleStatus.STABLE_WEIGHT) {
      isWeight = false;
      quantity = 0;
    }
    return sellFoundItem(itemLookup, quantity, isWeight)
      .catch(popupMessage)
      .finally(finishReturnItem);
  };

  const pluCallback = async (selectedItem?: ISelectedItem, itemId?: string): Promise<void> => {
    if (selectedItem) {
      return sellPluItem(selectedItem)
        .catch(popupMessage)
        .finally(finishReturnItem);
    }
    if (itemId) {
      return addInputData(itemId)
        .catch(popupMessage)
        .then(finishReturnItem);
    }
  };

  const onScan = async (scannedData: string): Promise<void> => {
    const itemInputData: IItemInputData = formatScannedData(scannedData);
    return addItemToTransaction(itemInputData)
      .then(finishReturnItem)
      .catch(onError);
  };

  return operationNotify(ActivityType.ReturnItem)
    .then(() => {
      batch((): void => {
        store.dispatch(SalesKeypadActions.resetSalesKeypad());
        store.dispatch(POSModeActions.setReturnState(ReturnsType.NonReceiptReturn));
      });
    })
    .then(() => {
      setSearchCallback(searchCallback);
      setPluCallback(pluCallback);
      showReturnItemPage(
        {
          onKeypadSumbit,
          onCancel: finishReturnItem,
          onSearch,
        },
        onScan,
      );
    })
    .catch(popupMessage);
};
